import UIComponent from "sap/ui/core/UIComponent";
import Device from "sap/ui/Device";
import ODataModel from "sap/ui/model/odata/v4/ODataModel";
import JSONModel from "sap/ui/model/json/JSONModel";
import BindingMode from "sap/ui/model/BindingMode";
import OidcClient from "./auth/OidcClient";
import { loadOidcConfig } from "./auth/OidcConfig";
import AuthFetch from "./auth/AuthFetch";
import { BackendPermissionsSource, type Permissions, type MeResponse } from "./auth/Permissions";
import { setProblemBundle } from "./util/ProblemDetails";
import { redirectWhileGated, resolveInitialHash, resolvePortalGate, type PortalGateRoute } from "./auth/PortalGate";
import type ResourceModel from "sap/ui/model/resource/ResourceModel";

/**
 * @namespace computec.b2b.portal
 */
export default class Component extends UIComponent {
	public static metadata = {
		manifest: "json",
		interfaces: ["sap.ui.core.IAsyncContentCreation"]
	};

	private contentDensityClass: string;
	private oidc?: OidcClient;
	private authFetch?: AuthFetch;
	private permissions: BackendPermissionsSource;

	/**
	 * Resolves once the user is signed in and /api/v1/me has filled the user model - the point from
	 * which App.controller may judge the user (pending invitation, staff sign-in, license-only).
	 * With IAsyncContentCreation the root view is created inside super.init(), before sign-in, so
	 * reading the user model in App.onInit is too early. Never resolves on the authError path.
	 */
	public bootstrapped!: Promise<void>;
	private resolveBootstrapped!: () => void;

	public static readonly SESSION_KEY_INVITATION_TOKEN = "portal.invitation.token";

	public init(): void {
		// Must precede super.init(): it creates the root view, whose onInit already reads `bootstrapped`.
		this.bootstrapped = new Promise<void>((resolve) => {
			this.resolveBootstrapped = resolve;
		});

		super.init();

		this.setModel(this.createDeviceModel(), "device");

		// Error codes become sentences only once this bundle is registered. Done here rather than in
		// each of the ninety-one call sites of problemMessage, so a code gains its message by adding
		// an i18n key and nothing else.
		const i18nModel = this.getModel("i18n") as ResourceModel;
		void Promise.resolve(i18nModel.getResourceBundle())
			.then((bundle) => { setProblemBundle(bundle); });

		// ADR-017 §1.7: anonymous license-only intercept przed OIDC. KC executeActionsEmail
		// wraca tu z `/index.html?token=…#/accept-invitation`. Dla license-only userów
		// (HasPortalAccess=false) token z URL jest jedynym sekretem — wołamy publiczny endpoint
		// `/api/v1/users/accept-invitation-by-token` BEZ JWT, dostajemy `redirectUrl` (Luna),
		// robimy hard nav out. NIGDY nie odpalamy OIDC dla licensed usera — żadnego login formu.
		//
		// Endpoint odrzuca portal userów (Domain rzuca błąd) → SPA fallbackuje na standardowy
		// OIDC flow + authenticated /accept-invitation (z JWT sub-check).
		//
		// Token persistujemy w sessionStorage ZANIM cokolwiek się dzieje — gdy fallbackujemy do
		// OIDC, KC roundtrip wyczyści `?token` z URL, ale sessionStorage przeżyje (Component init
		// fires again po OIDC callback, anonymous endpoint już został zatwierdzony / odrzucony,
		// więc nie wołamy go ponownie).
		const tokenFromUrl = this._readInvitationTokenFromUrl();
		if (tokenFromUrl) {
			try { window.sessionStorage.setItem(Component.SESSION_KEY_INVITATION_TOKEN, tokenFromUrl); } catch { /* private mode */ }
			// Sprawdź czy nie jesteśmy w środku OIDC callback (`?code=...&state=...`) — wtedy
			// pomijamy anonymous attempt (token już jest w session od poprzedniego loadu).
			const search = new URLSearchParams(window.location.search);
			if (!search.has("code") && !search.has("state")) {
				// Fire-and-forget, ale ZANIM utworzymy OidcClient. Jeśli endpoint zwróci redirectUrl
				// — robimy window.location.assign() i bootstrap się nigdy nie skończy. Jeśli zwróci
				// błąd (portal user / expired / not found) — kontynuujemy standardowy OIDC bootstrap.
				void this._tryAnonymousAcceptInvitation(tokenFromUrl);
				// Nie return'ujemy z init() — OIDC bootstrap startuje równolegle.
				// W praktyce dla licensed usera anonymous endpoint odpowiada szybciej niż KC
				// authorize redirect, więc window.location.assign przerwie wszystko.
			}
		}

		void loadOidcConfig()
			.then((config) => {
				this.oidc = new OidcClient(config);
				this.authFetch = new AuthFetch(this.oidc);
				return this.oidc.ensureAuthenticated();
			})
			.then(async (accessToken) => {
				this.applyAuthToOData(accessToken);
				// OidcClient odświeża token w tle co ~4-5 min; bez tego nasłuchiwacza
				// ODataModel wisi ze starym Authorization w nagłówkach i każde kolejne
				// $batch / $count po pierwszym refresh dostaje 401.
				this.oidc.onTokenRefreshed(token => this.applyAuthToOData(token));
				this.attachODataReloginOn401();
				this.permissions = new BackendPermissionsSource(
					() => this.authFetch.json<MeResponse>("/api/v1/me"));
				this.setModel(this.createUserModel(), "user");
				this.setModel(this.permissions.model(), "permissions");
				// Single /api/v1/me call: loads effective permissions (source of truth) AND enriches
				// the user model (organizationId — needed by Deployments and License binding wizards).
				// Awaited BEFORE router.initialize() so views never render with an unpopulated
				// permissions model (byCode is fail-closed, but this avoids any flash).
				await this.loadMeAsync();
				// The gate is decided before the router starts, so the view behind the current hash
				// (dashboard, a deep link) is never built for an account that may not use the portal.
				const gate = this.applyPortalGate();
				const hash = resolveInitialHash(gate, window.location.hash);
				if (hash !== window.location.hash.replace(/^#\/?/, "")) {
					window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#/${hash}`);
				}
				// A hash typed by hand while gated leads back to the gate screen. Guarded in parse, before
				// any route matches: a navTo from beforeRouteMatched would not stop the typed route's view.
				const router = this.getRouter();
				const parse = router.parse.bind(router);
				router.parse = (newHash: string): void => {
					const current = (this.getModel("user") as JSONModel).getProperty("/portalGate") as PortalGateRoute | "";
					if (current && redirectWhileGated(current, newHash) !== null) {
						router.navTo(current, {}, undefined, true);
						return;
					}
					parse(newHash);
				};
				this.resolveBootstrapped();
				this.getRouter().initialize();
			})
			.catch((err: unknown) => {
				console.error("[Portal] bootstrap failed", err);
				const msg = err instanceof Error
					? err.message
					: typeof err === "string"
						? err
						: JSON.stringify(err);
				// Stash detail for AuthError view (sessionStorage = survives the route navTo).
				window.sessionStorage.setItem("portal.authError.detail", msg);
				// Initialize router so authError view can render in mainContents
				// (without init the NavContainer is empty and we'd see a blank screen).
				// The hash is set first: initialize() matches the current hash, and an empty one would
				// build the dashboard, which needs the AuthFetch that a failed configuration never created.
				window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#/auth-error`);
				this.getRouter().initialize();
				this.getRouter().navTo("authError", {}, undefined, true);
			});
	}

	/** Undefined until the OIDC configuration is loaded, and for good when loading it failed (authError route). */
	public getOidc(): OidcClient | undefined {
		return this.oidc;
	}

	public getAuthFetch(): AuthFetch {
		if (!this.authFetch) {
			throw new Error("AuthFetch is used before the OIDC configuration was loaded.");
		}
		return this.authFetch;
	}

	public getPermissions(): Permissions {
		return this.permissions;
	}

	public getContentDensityClass(): string {
		if (this.contentDensityClass === undefined) {
			if (document.body.classList.contains("sapUiSizeCozy") || document.body.classList.contains("sapUiSizeCompact")) {
				this.contentDensityClass = "";
			} else if (!Device.support.touch) {
				this.contentDensityClass = "sapUiSizeCompact";
			} else {
				this.contentDensityClass = "sapUiSizeCozy";
			}
		}
		return this.contentDensityClass;
	}

	private applyAuthToOData(accessToken: string): void {
		const odata = this.getModel("odata") as ODataModel | undefined;
		if (odata && typeof (odata as unknown as { changeHttpHeaders?: (h: Record<string, string>) => void }).changeHttpHeaders === "function") {
			const headers: Record<string, string> = { Authorization: `Bearer ${accessToken}` };
			const lang = this.preferredLanguage();
			if (lang) {
				headers["Accept-Language"] = lang;
			}
			(odata as unknown as { changeHttpHeaders: (h: Record<string, string>) => void })
				.changeHttpHeaders(headers);
		}
	}

	/**
	 * Rejestruje handler na manifest-skonfigurowanym ODataModel — gdy backend zwróci 401
	 * (token expired, sub mismatch po reimporcie KC realm, etc.), wymusza interactive
	 * re-login zamiast zostawiać użytkownika w stuck stanie z błędami w UI.
	 *
	 * ODataModel v4 emituje 'messageChange' / 'requestFailed' z parameterem statusCode.
	 */
	private attachODataReloginOn401(): void {
		const odata = this.getModel("odata") as ODataModel | undefined;
		if (!odata) {
			return;
		}
		// HTTP errors w OData v4 model trafiają do attachable 'sessionTimeout' (KC 401 dla expired)
		// LUB do generic 'messageChange'. Najpewniejsze: hook do `attachSessionTimeout` (UI5 1.111+).
		// Jeśli SAP not surface'uje API, fallback: monitor Message manager.
		const anyOdata = odata as unknown as {
			attachSessionTimeout?: (handler: () => void) => void;
		};
		if (typeof anyOdata.attachSessionTimeout === "function") {
			anyOdata.attachSessionTimeout(() => {
				void this.oidc.forceRelogin();
			});
		}
	}

	private preferredLanguage(): string | null {
		try {
			const saved = window.localStorage?.getItem("portalLang");
			if (saved === "pl" || saved === "en") {
				return saved;
			}
		} catch {
			/* private mode */
		}
		return null;
	}

	private createDeviceModel(): JSONModel {
		const model = new JSONModel(Device);
		model.setDefaultBindingMode(BindingMode.OneWay);
		// Device flags (system.phone/tablet/desktop, resize, orientation) zmieniają się po przełączeniu
		// responsive mode w DevTools / resize okna. JSONModel(Device) jest jednorazowym snapshot'em —
		// bez tych listenerów ToolPage (sideExpanded) i inne layouty zostają w starym stanie po toggle.
		Device.media.attachHandler(() => model.refresh(true), null, Device.media.RANGESETS.SAP_STANDARD);
		Device.resize.attachHandler(() => model.refresh(true));
		Device.orientation.attachHandler(() => model.refresh(true));
		return model;
	}

	/**
	 * Single `/api/v1/me` round-trip: {@link BackendPermissionsSource.load} fetches it, applies the
	 * effective permission set to the `permissions` model, and returns the payload, which we reuse
	 * here to enrich the `user` model. On failure the permissions model stays empty (fail-closed —
	 * gated controls hidden) and `meKnown` stays false so a transient error does not wrongly gate a
	 * legitimate user onto NoPortalAccess.
	 */
	private async loadMeAsync(): Promise<void> {
		try {
			const me = await this.permissions.load();
			const userModel = this.getModel("user") as JSONModel | undefined;
			if (userModel) {
				userModel.setProperty("/portalUserId", me.portalUserId ?? "");
				userModel.setProperty("/orgId", me.organizationId ?? "");
				userModel.setProperty("/orgName", me.organizationName ?? "");
				// ADR-017 §2: App.controller.onInit czyta `/status` żeby wykryć Pending
				// (KC redirect z executeActionsEmail) i auto-firuje accept-invitation.
				userModel.setProperty("/status", me.status ?? "");
				// ADR-018 §5: App.controller.onInit czyta `/hasPortalAccess` żeby wykryć
				// license-only userów (Luna AI / WebUp end-users) i navTo noPortalAccess
				// PRZED Pending detection. Wartość `null/undefined` z /me oznacza że user
				// nie jest jeszcze rozpoznany w portal-bazie — traktujemy jak "nie blokuj",
				// żeby flow accept-invitation mógł dalej działać.
				userModel.setProperty("/hasPortalAccess", me.hasPortalAccess ?? null);
				// Card 31: CompuTec staff signed in with a password are sent to the Microsoft sign-in view.
				userModel.setProperty("/staffLoginRequiresBroker", me.staffLoginRequiresBroker === true);
				// meKnown = /me answered (even when the user has no portal record yet -> null).
				// resolvePortalGate gates only when meKnown is true: after a transient /me error (catch
				// below, meKnown stays false) a legitimate user is not sent to NoPortalAccess.
				userModel.setProperty("/meKnown", true);
			}
		} catch (e) {
			console.warn("[Portal] failed to enrich user model with /api/v1/me:", e);
		}
	}

	/**
	 * Re-fetches `/api/v1/me` po auto-accept w App.controller — odświeża effective permissions
	 * (newly accepted user przechodzi z Pending → Active, ale permissions już są w access tokenie).
	 * Zwraca świeży status, aby caller mógł sprawdzić czy promotion się powiodła.
	 */
	public async refreshUserModelAsync(): Promise<void> {
		await this.loadMeAsync();
	}

	/**
	 * Evaluates the portal gate from the user model and the pending invitation token, stores it in
	 * `user>/portalGate` (empty when not gated; the shell hides its navigation on it) and returns it.
	 */
	public applyPortalGate(): PortalGateRoute | null {
		const userModel = this.getModel("user") as JSONModel;
		let invitationToken: string | null = null;
		try {
			invitationToken = window.sessionStorage.getItem(Component.SESSION_KEY_INVITATION_TOKEN);
		} catch {
			/* private mode */
		}
		const gate = resolvePortalGate({
			meKnown: userModel.getProperty("/meKnown") === true,
			hasPortalAccess: userModel.getProperty("/hasPortalAccess") as boolean | null | undefined,
			staffLoginRequiresBroker: userModel.getProperty("/staffLoginRequiresBroker") === true,
			invitationPending: !!invitationToken
		});
		userModel.setProperty("/portalGate", gate ?? "");
		return gate;
	}

	private createUserModel(): JSONModel {
		const idClaims = this.oidc.getIdTokenClaims() ?? {};
		const accessClaims = this.oidc.getAccessTokenClaims() ?? {};
		const realmAccess = (accessClaims["realm_access"] ?? idClaims["realm_access"]) as { roles?: string[] } | undefined;
		const model = new JSONModel({
			name: idClaims["name"] ?? idClaims["preferred_username"] ?? accessClaims["name"] ?? accessClaims["preferred_username"] ?? "",
			email: idClaims["email"] ?? accessClaims["email"] ?? "",
			subject: idClaims["sub"] ?? accessClaims["sub"] ?? "",
			roles: realmAccess?.roles ?? [],
			// Uzupełniane przez enrichUserModelAsync z /api/v1/me. meKnown=false dopóki /me nie odpowie.
			hasPortalAccess: null,
			meKnown: false,
			portalGate: ""
		});
		model.setDefaultBindingMode(BindingMode.OneWay);
		return model;
	}

	private _readInvitationTokenFromUrl(): string | null {
		try {
			return new URLSearchParams(window.location.search).get("token");
		} catch {
			return null;
		}
	}

	/**
	 * ADR-017 §1.7: probe publicznego endpointu acceptance dla license-only userów. Wywoływany
	 * przed OIDC bootstrapem żeby uniknąć portal login formu dla userów którzy w portalu
	 * niczego nie robią (idą tylko na Lunę). Response 2xx z `redirectUrl` → hard nav out;
	 * inny status → fallback do standardowego OIDC + authenticated /accept-invitation flow.
	 */
	private async _tryAnonymousAcceptInvitation(token: string): Promise<void> {
		try {
			const resp = await fetch("/api/v1/users/accept-invitation-by-token", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ invitationToken: token })
			});
			if (!resp.ok) {
				// 4xx = portal user / wygasło / nie znaleziono — fallback do OIDC flow, który
				// dzieje się równolegle (init() już go wystartował).
				return;
			}
			const body = await resp.json() as { userId: string; redirectUrl: string | null };
			if (body.redirectUrl) {
				try { window.sessionStorage.removeItem(Component.SESSION_KEY_INVITATION_TOKEN); } catch { /* ignore */ }
				window.location.assign(body.redirectUrl);
				// Promise nigdy nie resolwuje — browser nawiguje out.
			}
		} catch (err) {
			console.warn("[Portal] anonymous accept-invitation probe failed, falling back to OIDC:", err);
		}
	}
}
